import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Package } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api, money } from "@/lib/api";

export default function Account() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem("dr_customer_token")) {
      navigate("/login");
      return;
    }
    api.customerMe()
      .then((u) => {
        setUser(u);
        return api.customerOrders().then(setOrders).catch(() => {});
      })
      .catch(() => {
        localStorage.removeItem("dr_customer_token");
        navigate("/login");
      })
      .finally(() => setChecking(false));
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("dr_customer_token");
    localStorage.removeItem("dr_customer_email");
    localStorage.removeItem("dr_customer_name");
    navigate("/");
  };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center pt-16" data-testid="account-loading">
      <p className="font-mono text-xs tracking-[0.4em] text-neutral-500 animate-pulse">LOADING...</p>
    </div>;
  }

  return (
    <div data-testid="account-page">
      <PageHeader overline="MEMBER ACCOUNT" title={user?.name ? `Hey, ${user.name.split(" ")[0]}` : "Your Account"} sub={user?.email} />
      <div className="mx-auto max-w-[1100px] px-5 sm:px-8 py-14">
        <div className="flex items-center justify-between border border-wire bg-charcoal/40 px-6 py-5">
          <div className="flex items-center gap-3">
            <Package size={16} className="text-neutral-400" />
            <p className="font-mono text-[11px] tracking-[0.3em] text-neutral-300">ORDER HISTORY</p>
          </div>
          <button data-testid="account-logout-button" onClick={logout}
            className="flex items-center gap-2 border border-wire px-4 py-2 font-mono text-[10px] tracking-[0.25em] text-neutral-400 transition-colors hover:border-white hover:text-white">
            <LogOut size={12} /> LOG OUT
          </button>
        </div>

        <div className="border border-t-0 border-wire" data-testid="account-orders">
          {orders.length === 0 ? (
            <div className="px-8 py-20 text-center" data-testid="account-no-orders">
              <p className="font-display text-xl font-extrabold uppercase tracking-tight text-neutral-400">No orders yet</p>
              <p className="mt-3 font-mono text-[11px] tracking-[0.25em] text-neutral-600">THE OUTFIT AWAITS.</p>
              <Link to="/shop" data-testid="account-shop-link"
                className="mt-8 inline-block bg-white px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300">
                SHOP NOW
              </Link>
            </div>
          ) : (
            orders.map((o) => (
              <div key={o.order_number} data-testid={`account-order-${o.order_number}`} className="flex flex-wrap items-center gap-4 border-b border-wire px-6 py-5 last:border-b-0">
                <p className="font-mono text-sm tracking-[0.15em] text-white">{o.order_number}</p>
                <div className="min-w-[200px] flex-1">
                  <p className="font-mono text-[11px] text-neutral-300">
                    {(o.items || []).map((i) => `${i.name} (${i.size}) x${i.qty}`).join(" · ")}
                  </p>
                  <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-neutral-500">
                    {(o.created_at || "").slice(0, 10)} — {o.estimated_delivery || "5–7 BUSINESS DAYS"}
                  </p>
                </div>
                <p className="font-mono text-sm text-neutral-200">{money(o.total || o.subtotal)}</p>
                <span className={`border px-3 py-1.5 font-mono text-[9px] tracking-[0.25em] ${o.status === "paid" ? "border-white text-white" : "border-wire text-neutral-500"}`}>
                  {o.status === "paid" ? "CONFIRMED" : "AWAITING PAYMENT"}
                </span>
                <Link to="/track" data-testid={`account-track-${o.order_number}`}
                  className="font-mono text-[10px] tracking-[0.25em] text-neutral-400 underline underline-offset-4 hover:text-white">
                  TRACK
                </Link>
              </div>
            ))
          )}
        </div>
        <p className="mt-6 font-mono text-[10px] leading-relaxed tracking-[0.2em] text-neutral-600">
          ORDERS PLACED AS A GUEST WITH THIS EMAIL SHOW UP HERE TOO.
        </p>
      </div>
    </div>
  );
}
